import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ServiceDeal } from 'services/service.deal';
import ServiceEventTracking from 'services/service.event-tracking';
import {
  ContactFormFieldsValidationSchema,
  FORM_FIELDS_DEFAULT_VALUES
} from './ContactForm.constants';
import { ContactFormFields, UseContactForm } from './ContactForm.types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const useContactForm: UseContactForm = () => {
  const { t } = useI18n('contact');
  const { locale } = useRouter();
  const { addErrorToast, addSuccessToast } = useToasts();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formMethods = useForm<ContactFormFields>({
    defaultValues: FORM_FIELDS_DEFAULT_VALUES,
    resolver: zodResolver(ContactFormFieldsValidationSchema)
  });

  const isTermsAccepted = !!formMethods.watch('terms');

  const onSubmit = useCallback(
    async (data: ContactFormFields) => {
      try {
        if (!executeRecaptcha) {
          console.error('Recaptcha not available');
          return;
        }

        const recaptchaToken = await executeRecaptcha('public_contact_form');
        await ServiceDeal.create({
          ...data,
          howDoYouKnowUs: data.howDoYouKnowUs || 'OTHER',
          source: 'Manfred Web',
          consultingOptions: [],
          recaptchaToken
        });

        formMethods.reset(FORM_FIELDS_DEFAULT_VALUES);
        addSuccessToast(t('contact.form.success'));
      } catch (ex) {
        console.error(
          `There was an error while submitting the Contact form. Form data: ${data}. Exception: ${ex}`
        );

        addErrorToast(t('contact.form.error'));
      }
    },
    [addErrorToast, addSuccessToast, formMethods, executeRecaptcha, t]
  );

  const onFormFieldFocus = useCallback(() => {
    ServiceEventTracking.track({
      type: 'Landing - Form Focused',
      properties: {
        objectName: 'contacto',
        url: window.location.href,
        urlParams: new URLSearchParams(window.location.search).toString()
      }
    });
  }, []);

  return {
    t,
    locale,
    formMethods,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  };
};
